/**
 * Utility functions for form handling
 */

// نگاشت نام نقش‌ها از انگلیسی به فارسی
const roleNameMap = {
  'admin': 'مدیر',
  'employee': 'کارمند',
  'client': 'مشتری',
  'app administrator': 'مدیر سیستم',
  'manager': 'مدیر',
  'super admin': 'مدیر ارشد',
};

/**
 * Translates English role names to Persian
 * @param {string} name - Role name (display_name or name)
 * @returns {string} Persian name
 */
export const translateRoleName = (name) => {
  if (!name) return '--';
  const lower = name.toLowerCase();
  return roleNameMap[lower] || name;
};

/**
 * Scrolls to the first field with validation error
 * @param {FormInstance} form - Ant Design form instance
 */
export const scrollToFirstError = (form) => {
  try {
    const errorFields = form.getFieldsError();
    const firstErrorField = errorFields.find((field) => field.errors.length > 0);

    if (firstErrorField) {
      form.scrollToField(firstErrorField.name, {
        behavior: 'smooth',
        block: 'center',
      });
    }
  } catch (error) {
    console.error('Error scrolling to field:', error);
  }
};

/**
 * @param {Error|Object} error
 * @returns {Record<string, string|string[]>|null}
 */
export const getValidationDetails = (error) => {
  if (!error) return null;
  if (error.name === 'ValidationError' && error.details) {
    return error.details;
  }
  if (error.details && typeof error.details === 'object' && !Array.isArray(error.details)) {
    return error.details;
  }
  if (error.error?.details && typeof error.error.details === 'object') {
    return error.error.details;
  }
  return null;
};

/** @param {Error|Object} error */
export const isValidationError = (error) => !!getValidationDetails(error);

/**
 * @param {string} fieldName
 * @param {Record<string, string|string[]>} [fieldMap]
 */
export const parseApiFieldName = (fieldName, fieldMap = {}) => {
  if (fieldMap[fieldName] !== undefined) {
    const mapped = fieldMap[fieldName];
    if (Array.isArray(mapped)) return mapped;
    if (typeof mapped === 'string' && mapped.includes('.')) {
      return mapped.split('.').map((part) => {
        const num = Number(part);
        return Number.isInteger(num) && String(num) === part ? num : part;
      });
    }
    return mapped;
  }
  if (fieldName.includes('.')) {
    return fieldName.split('.').map((part) => {
      const num = Number(part);
      return Number.isInteger(num) && String(num) === part ? num : part;
    });
  }
  return fieldName;
};

/**
 * @param {Error|Object} error
 * @param {Record<string, string|string[]>} [fieldMap]
 */
export const buildFormFieldErrors = (error, fieldMap = {}) => {
  const details = getValidationDetails(error);
  if (!details) return [];

  return Object.keys(details).map((fieldName) => {
    const fieldErrors = Array.isArray(details[fieldName])
      ? details[fieldName]
      : [details[fieldName]];
    return {
      name: parseApiFieldName(fieldName, fieldMap),
      errors: fieldErrors.filter(Boolean).map(String),
    };
  });
};

/**
 * Applies API 422 validation errors to an Ant Design form and scrolls to the first error.
 * @returns {boolean} true if field errors were applied
 */
export const applyValidationErrorsToForm = (form, error, options = {}) => {
  const { fieldMap = {}, scroll = true } = options;
  if (!form) return false;

  const fieldsError = buildFormFieldErrors(error, fieldMap);
  if (fieldsError.length === 0) return false;

  form.setFields(fieldsError);

  if (scroll) {
    requestAnimationFrame(() => scrollToFirstError(form));
  }

  return true;
};

/**
 * Handles form submission with automatic scroll to first error
 * @param {FormInstance} form - Ant Design form instance
 * @param {Function} onSubmit - Submit handler function
 * @returns {Promise} - Promise that resolves when form is submitted successfully
 */
export const handleFormSubmit = async (form, onSubmit) => {
  try {
    const values = await form.validateFields();
    return await onSubmit(values);
  } catch (error) {
    // If validation fails, scroll to first error
    if (error.errorFields && error.errorFields.length > 0) {
      scrollToFirstError(form);
    }
    throw error;
  }
};
